import { Injectable } from '@angular/core';
import { ApiService } from '@core/services/api.service';
import { StateService } from '@core/services/state.service';
import { AuthenticationService } from 'app/auth/service';
import { BehaviorSubject, of, Observable, throwError } from 'rxjs';
import { switchMap, take, tap, catchError, map, withLatestFrom } from 'rxjs/operators';
import { Afakulcs, Column } from '../models/torzsek';

@Injectable({
  providedIn: 'root',
})
export class AfakulcsokService {
  private readonly apiEndpoint = 'afakulcsok';
  private readonly columnsEndpoint = 'afakulcsok/columns';

  private readonly _data = new BehaviorSubject<Afakulcs[]>(null);
  private readonly _selectedData = new BehaviorSubject<Afakulcs>(null);

  public data$ = this._data.pipe(
    switchMap((state: Afakulcs[]) => (state ? of(state) : this.fetchData())),
    map((data) =>
      data.sort((a: any, b: any) => a.nev.localeCompare(b.nev))
    )
  );

  constructor(private api: ApiService, private stateService: StateService, private authService: AuthenticationService) {}

  get columns$(): Observable<Column[]> {
    return this.getColumns();
  }

  get initData$(): Observable<Afakulcs[]> {
    return this._data.pipe(
      take(1),
      switchMap((state: Afakulcs[]) => (state ? of(state) : this.fetchData())),
      map((data) =>
        data.sort((a: any, b: any) => a.nev.localeCompare(b.nev))
      )
    );
  }

  add(data: Afakulcs): Observable<Afakulcs> {
    return this.api.post(this.apiEndpoint, data).pipe(
      tap((added: Afakulcs) =>
        this.stateService.setStateOnAdd(this.mapAfakulcs(added), this._data)
      ),
      catchError((err: any) => throwError(err))
    );
  }

  delete(id: number): Observable<Afakulcs> {
    return this.api.delete(this.apiEndpoint, id).pipe(
      tap((deleted: Afakulcs) =>
        this.stateService.setStateOnDelete(deleted, this._data)
      ),
      catchError((err: any) => throwError(err))
    );
  }

  getById(id: string): Observable<Afakulcs> {
    return this.data$.pipe(
      take(1),
      map((state) => {
        console.log(id, state);
        // Find the data
        const data = state.find((item: Afakulcs) => item.id === +id) || null;

        // Update the data
        this._selectedData.next(data);

        // Return the data
        return data;
      }),
      switchMap((data) => {
        if (id === 'new') {
          return of(null);
        }

        if (!data) {
          return throwError('Could not found data with id of ' + id + '!');
        }

        return of(data);
      })
    );
  }

  update(data: Afakulcs): Observable<Afakulcs> {
    return this.api.put(`${this.apiEndpoint}/${data.id}`, data).pipe(
      tap((updated: Afakulcs) =>
        this.stateService.setStateOnUpdate(
          this.mapAfakulcs(updated),
          this._data
        )
      ),
      catchError((err: any) => throwError(err))
    );
  }

  private fetchData(): Observable<Afakulcs[]> {
    return this.api.get(this.apiEndpoint).pipe(
      map((data: Afakulcs[]) => this.mapAfakulcsok(data)),
      tap((data: Afakulcs[]) => this._data.next(data)),
      catchError((err: any) => {
        console.log(err);
        return of([]);
      })
    );
  }

  private getColumns(): Observable<Column[]> {
    return this.api.get(this.columnsEndpoint).pipe(
      catchError((err: any) => {
        console.log(err);
        return of([]);
      })
    );
  }

  private mapAfakulcsok(afakulcsok: Afakulcs[]): Afakulcs[] {
    return afakulcsok.map((m: Afakulcs) => this.mapAfakulcs(m));
  }

  private mapAfakulcs(afakulcs: Afakulcs): Afakulcs {
    return {
      ...afakulcs,
      kezdete: new Date(afakulcs.kezdete),
      kezdeteStr: afakulcs.kezdete,
      lejarta: new Date(afakulcs.lejarta),
      lejartaStr: afakulcs.lejarta,
    };
  }
}
